import React, { useState } from 'react';
import { AlertTriangle, LogOut } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { toast } from 'sonner';

/**
 * Sticky amber/red banner that appears at the very top of every page when
 * an admin is impersonating another user. Clicking "Return to admin" hits
 * ``/api/auth/impersonate/return`` which restores the original cookies and
 * reloads the app.
 */
export function ImpersonationBanner() {
  const { user, refreshUser } = useAuth();
  const [returning, setReturning] = useState(false);
  const imp = user?.impersonation;

  if (!imp) return null;

  const handleReturn = async () => {
    setReturning(true);
    try {
      await api.endImpersonation();
      toast.success(`Returned to ${imp.actor_name || 'admin'} account`);
      // Hard reload so every page-level cache (auth/me, lists, sockets) is rebuilt
      window.location.assign('/dashboard');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not return to admin');
      setReturning(false);
      refreshUser?.();
    }
  };

  return (
    <div
      className="sticky top-0 z-[200] bg-amber-500 text-amber-950 border-b-2 border-amber-700 shadow-md"
      role="alert"
      data-testid="impersonation-banner"
    >
      <div className="max-w-screen-2xl mx-auto px-4 py-2 flex items-center gap-3 flex-wrap">
        <AlertTriangle className="w-4 h-4 shrink-0" />
        <p className="text-xs font-medium flex-1 min-w-0">
          <span className="font-bold uppercase tracking-wider mr-1.5">Impersonating</span>
          <span data-testid="impersonation-target">{user.name}</span>
          <span className="opacity-70 ml-2">· signed in as {imp.actor_name || imp.actor_email}</span>
        </p>
        <button
          onClick={handleReturn}
          disabled={returning}
          className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-950 hover:bg-black text-amber-100 rounded-sm text-xs font-medium transition-colors disabled:opacity-60"
          data-testid="return-to-admin-btn"
        >
          <LogOut className="w-3 h-3" />
          {returning ? 'Returning…' : 'Return to admin'}
        </button>
      </div>
    </div>
  );
}
